"use strict";
// CIP fork feature (see FORK.md): the blueprint judge's prompt, scales and
// parser, ported from weval-org/app src/cli/evaluators/llm-coverage-evaluator.ts
// (MIT, see ./pointFunctions/NOTICE.md). The wording is kept verbatim so new
// runs stay on one series with migrated weval history — including weval's
// quirk of using the 5-point class names in the system prompt's examples
// whatever scale is in use. The model call itself lives in the worker.
Object.defineProperty(exports, "__esModule", { value: true });
exports.JUDGE_PARSER_VERSION = exports.JUDGE_USER_SUFFIX_TEMPLATE = exports.JUDGE_USER_PREFIX_TEMPLATE = exports.JUDGE_SYSTEM_TEMPLATE = exports.JUDGE_MAX_OUTPUT_TOKENS = exports.JUDGE_TEMPERATURE = exports.JUDGE_SCALE_CLASSES = void 0;
exports.renderTranscript = renderTranscript;
exports.judgeSuiteDescriptionBlock = judgeSuiteDescriptionBlock;
exports.judgeCriterionVariables = judgeCriterionVariables;
exports.buildJudgeSystemPrompt = buildJudgeSystemPrompt;
exports.buildJudgeUserPrompt = buildJudgeUserPrompt;
exports.parseJudgeResponse = parseJudgeResponse;
const compile_1 = require("../cip-system-prompts/compile");
const WEVAL_5 = [
    { name: "CLASS_UNMET", score: 0.0, description: "The criterion is not met." },
    {
        name: "CLASS_PARTIALLY_MET",
        score: 0.25,
        description: "The criterion is partially met.",
    },
    {
        name: "CLASS_MODERATELY_MET",
        score: 0.5,
        description: "The criterion is moderately met.",
    },
    {
        name: "CLASS_MAJORLY_MET",
        score: 0.75,
        description: "The criterion is mostly met.",
    },
    {
        name: "CLASS_EXACTLY_MET",
        score: 1.0,
        description: "The criterion is fully met.",
    },
];
const WEVAL_9 = [
    {
        name: "CLASS_UTTERLY_UNMET",
        score: 0.0,
        description: "The criterion is so completely absent to an extent where the content in-fact *contradicts* or contravenes the criterion.",
    },
    {
        name: "CLASS_UNMET",
        score: 0.001,
        description: "The criterion is not met.",
    },
    {
        name: "CLASS_TRACE",
        score: 0.125,
        description: "Only a trace or hint of the criterion appears.",
    },
    {
        name: "CLASS_SLIGHT",
        score: 0.25,
        description: "A slight presence of the criterion is detectable.",
    },
    {
        name: "CLASS_PARTIAL",
        score: 0.375,
        description: "Partial fulfillment; important elements are missing.",
    },
    {
        name: "CLASS_MODERATE",
        score: 0.5,
        description: "Moderate fulfillment; balanced presence with notable gaps.",
    },
    {
        name: "CLASS_SUBSTANTIAL",
        score: 0.625,
        description: "Substantial fulfillment; most key aspects are present.",
    },
    {
        name: "CLASS_MAJOR",
        score: 0.75,
        description: "Major fulfillment; minor omissions remain.",
    },
    {
        name: "CLASS_VERY_NEARLY",
        score: 0.875,
        description: "Very nearly fully met; only negligible details missing.",
    },
    {
        name: "CLASS_EXACT",
        score: 1.0,
        description: "Exactly and fully meets the criterion.",
    },
];
const BINARY = [
    { name: "CLASS_UNMET", score: 0.0, description: "The criterion is not met." },
    { name: "CLASS_MET", score: 1.0, description: "The criterion is met." },
];
exports.JUDGE_SCALE_CLASSES = {
    "weval-9": WEVAL_9,
    "weval-5": WEVAL_5,
    binary: BINARY,
};
/** Judge settings weval used for every call. */
exports.JUDGE_TEMPERATURE = 0;
exports.JUDGE_MAX_OUTPUT_TOKENS = 500;
/**
 * The conversation as the judge reads it: one block per turn, assistant turns
 * labelled generated or fixed by their position among assistant turns.
 */
function renderTranscript(messages, generatedAssistantIndices) {
    const generated = new Set(generatedAssistantIndices);
    let assistantTurn = 0;
    return messages
        .map((m) => {
        if (m.role === "assistant") {
            const label = generated.has(assistantTurn)
                ? "assistant (generated)"
                : "assistant (fixed)";
            assistantTurn++;
            return `${label}: ${m.content ?? ""}`;
        }
        return `${m.role}: ${m.content ?? ""}`;
    })
        .join("\n--------------------\n");
}
const WEVAL_5_NAMES = WEVAL_5.map((c) => c.name);
// The judge's prompt as three Studio system-prompt templates (see
// ../cip-system-prompts): the system message, and the user message in two
// parts — the transcript prefix shared by every criterion of a cell, which a
// provider's prompt cache can reuse, and the criterion suffix. The server's
// registry (packages/shared/src/server/cip/systemPrompts) provisions these
// into every project as `studio/blueprints/judge`; a run resolves that
// prompt and falls back to these templates when it is missing.
exports.JUDGE_SYSTEM_TEMPLATE = `
You are an expert evaluator and examiner. You are incredibly precise. Your task is to assess how well a specific criterion is covered by a given text by providing a reflection and a precise classification.
Focus solely on the provided criterion, the text, and the classification guidelines. Adhere strictly to the XML output format specified in the user prompt. Be brief if possible.

{{suite_description}}

The criterion is an assertion being made about the <TEXT>. It might be allude to a 'response', which relates to the <TEXT>. It might be phrased in various ways. So some valid variations of a criterion given the text "The lemonade stand was open for 10 hours." might be:

- "The response should have included opening times and be concise"
- "Must include opening times and be concise"
- "Should be concise and include opening times"
- "Be concise and include opening times"
- "Not forget to include opening times and not forget to be concise"
- "Concise, w/ opening times"

(These are all equivalent ways to express the same criterion)

Here are examples of correct outputs:

-----
Input: <TEXT>The people in the room were not happy.</TEXT><CRITERION>The people in the room were happy.</CRITERION>
Output: <reflection>The text states that the people in the room were NOT happy. However, the criterion is that the people in the room WERE happy, which means the criterion is entirely UNMET.</reflection><classification>${WEVAL_5_NAMES[0]}</classification>
-----
Input: <TEXT>The population is 8,000,000</TEXT><CRITERION>The population is 8,019,828</CRITERION>
Output: <reflection>The number provided in the text is very close to the number in the criterion. Therefore, the criterion is nearly met, i.e. ${WEVAL_5_NAMES[3]}.</reflection><classification>${WEVAL_5_NAMES[3]}</classification>
-----
Input: <TEXT>Handling a situation where a colleague consistently takes credit for your ideas requires a balance of assertiveness, empathy, and strategic communication</TEXT><CRITERION>Mentions empathy</CRITERION>
Output: <reflection>The text mentions empathy, which means the criterion is MET precisely.</reflection><classification>${WEVAL_5_NAMES[WEVAL_5_NAMES.length - 1]}</classification>
-----
        `.trim();
exports.JUDGE_USER_PREFIX_TEMPLATE = `
You are given a multi-turn conversation transcript and a criterion. Evaluate ONLY the content produced by the candidate model (marked as [Assistant (generated)]). Do not give credit for [Assistant (fixed)] turns.

<TRANSCRIPT>
{{transcript}}
</TRANSCRIPT>

<TEXT>
{{response}}
</TEXT>
`;
exports.JUDGE_USER_SUFFIX_TEMPLATE = `
Carefully assess how well THIS SPECIFIC <CRITERION> is expressed in the generated assistant content:
<CRITERION>
{{criterion}}
</CRITERION>


Your task is to provide a 'reflection' and a 'classification' based on the guidelines below. Your classification should indicate which class the text falls into, according to the wording of the criterion.

Classification Guidelines:
{{guidelines}}

Your output MUST strictly follow this XML format:
<reflection>Your 1-2 sentence reflection and reasoning for the classification, explaining how well the criterion is met, if at all.</reflection>
<classification>ONE of the {{class_count}} class names (e.g., {{example_class}})</classification>
`;
/** The `{{suite_description}}` block: weval's context paragraph, or nothing. */
function judgeSuiteDescriptionBlock(suiteDescription) {
    return suiteDescription && suiteDescription.trim() !== ""
        ? `
For context, this evaluation is part of a broader suite with the following description (please recognize this merely as context, and not a part of the evaluation itself):

<BROAD_EVALUATION_DESCRIPTION>
${suiteDescription}
</BROAD_EVALUATION_DESCRIPTION>
`
        : "";
}
/** The suffix's variables for a criterion on a scale. */
function judgeCriterionVariables(params) {
    const classes = exports.JUDGE_SCALE_CLASSES[params.scale];
    return {
        criterion: params.criterionText,
        guidelines: classes.map((c) => `- ${c.name}: ${c.description}`).join("\n"),
        class_count: String(classes.length),
        example_class: classes[classes.length - 1].name,
    };
}
/**
 * The system prompt. It depends only on the suite description, so it is the
 * same for every call in a run and caches across them.
 */
function buildJudgeSystemPrompt(suiteDescription) {
    return (0, compile_1.compileStudioPromptText)(exports.JUDGE_SYSTEM_TEMPLATE, {
        suite_description: judgeSuiteDescriptionBlock(suiteDescription),
    });
}
/**
 * The user prompt, in two parts. `prefix` holds the transcript and the
 * response and is identical for every criterion of a cell, so a provider's
 * prompt cache can reuse it; `suffix` holds the criterion and guidelines.
 */
function buildJudgeUserPrompt(params) {
    // Untrimmed, as weval sends them: the newline each part carries at its
    // boundary is what separates the transcript from the criterion.
    return {
        prefix: (0, compile_1.compileStudioPromptText)(exports.JUDGE_USER_PREFIX_TEMPLATE, { transcript: params.transcript, response: params.responseText }, { trim: false }),
        suffix: (0, compile_1.compileStudioPromptText)(exports.JUDGE_USER_SUFFIX_TEMPLATE, judgeCriterionVariables(params), { trim: false }),
    };
}
/** Reads a judge's XML answer, forgiving a missing `CLASS_` prefix as weval does. */
function parseJudgeResponse(text, scale) {
    if (!text || text.trim() === "")
        return { error: "Empty response" };
    const reflection = text.match(/<reflection>([\s\S]*?)<\/reflection>/);
    const classification = text.match(/<classification>([\s\S]*?)<\/classification>/);
    if (!reflection || !classification) {
        return {
            error: `Failed to parse XML. Response: ${text.substring(0, 100)}...`,
        };
    }
    let name = classification[1].trim().toUpperCase();
    if (!name.startsWith("CLASS_"))
        name = `CLASS_${name}`;
    const match = exports.JUDGE_SCALE_CLASSES[scale].find((c) => c.name === name);
    if (!match) {
        return {
            error: `Invalid classification value: '${classification[1].trim()}'`,
        };
    }
    return {
        extent: match.score,
        reflection: reflection[1].trim(),
        classification: name,
    };
}
/**
 * The version of the verdict parsing a run's manifest records; bump it when a change
 * here would move a score.
 */
exports.JUDGE_PARSER_VERSION = "1";
